import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Typography, Card } from "@mui/material";
import {
  AddAPhoto as AddAPhotoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

// Componentes SUI
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import FileDialog from "components/FileDialog"; // Importar FileDialog para el modal

// API
import BucketApi from "api/bucketApi";
import ProcesosApi from "api/procesosApi";

const DocumentoAnexo = ({
  docAnexo,
  procesoId,
  identificador,
  onDelete,
  setError,
  isNew = false,
}) => {
  const [documentoUrl, setDocumentoUrl] = useState("");
  const [hasDocumento, setHasDocumento] = useState(false);
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false); // Estado para el modal de PDF

  const nombreNormalizadoUPPER = docAnexo.tipo
    .normalize("NFD") // Descompone letras acentuadas
    .replace(/[\u0300-\u036f]/g, "") // Elimina los signos diacríticos
    .replace(/ñ/g, "n") // Reemplaza ñ
    .replace(/Ñ/g, "N") // Reemplaza Ñ
    .replace(/\s+/g, "_") // Reemplaza espacios por guiones bajos
    .toUpperCase();

  const nombreNormalizadoLOWER = docAnexo.tipo
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ñ/g, "n")
    .replace(/Ñ/g, "N")
    .replace(/\s+/g, "_")
    .toLowerCase();

  const getDocumentoFileName = () => {
    return `doc_anexo_${nombreNormalizadoLOWER}.pdf`;
  };

  const verificarDocumento = async () => {
    if (!identificador) return;
    try {
      // Verificar si existe en anexos
      const anexoResponse = await ProcesosApi.verificarAnexoProceso(procesoId, {
        tipo_anexo: `DOC_ANEXO_${nombreNormalizadoUPPER}`,
      });
      // Verificar si existe el archivo
      const fileResponse = await BucketApi.checkSolicitudFileExists(
        getDocumentoFileName(),
        identificador
      );
      setHasDocumento(anexoResponse.data.exists && fileResponse.data.exists);
      if (anexoResponse.data.exists && fileResponse.data.exists) {
        //const urlResponse = await BucketApi.getSolicitudFileUrl(getDocumentoFileName(), identificador);
        const urlResponse = await BucketApi.getSolicitudFileUrlFresh(
          getDocumentoFileName(),
          identificador
        );
        setDocumentoUrl(urlResponse.data.file_url);
      }
    } catch (error) {
      console.error("Error verificando el Documento Anexo:", error);
      setHasDocumento(false);
    }
  };

  useEffect(() => {
    if (!isNew) {
      verificarDocumento();
    }
  }, [identificador, docAnexo.tipo, isNew]);

  const handleDocumentoChange = async (event) => {
    const file = event.target.files[0];
    if (!file || !identificador) return;
    setIsLoading(true);
    try {
      if (hasDocumento) {
        // Si hay documento, eliminar archivo del bucket
        await BucketApi.deleteSolicitudFile(getDocumentoFileName(), identificador);
        await ProcesosApi.eliminarAnexoProceso(procesoId, docAnexo.id);
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_name", getDocumentoFileName());
      formData.append("file_type", "solicitud");
      formData.append("proceso_id", identificador);
      formData.append("registrar_anexo", "true");
      formData.append("tipo_anexo", `DOC_ANEXO_${docAnexo.tipo.toUpperCase()}`);
      formData.append("nombre_anexo", docAnexo.nombre_anexo);
      formData.append("incluir_en_pdf", "True");
      const response = await BucketApi.uploadFile(formData);
      if (response.data?.file_url) {
        setHasDocumento(true);
        setDocumentoUrl(response.data.file_url);
        // Verificar que todo se registró correctamente
        await verificarDocumento();
      }
    } catch (error) {
      console.error("Error subiendo el Documento Anexo:", error);
      setError(`Error al subir el Documento Anexo de ${docAnexo.tipo}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!identificador || !window.confirm("¿Está seguro(a) de eliminar este Documento Anexo?"))
      return;
    setIsLoading(true);
    try {
      if (hasDocumento) {
        // Si hay documento, eliminar archivo del bucket
        await BucketApi.deleteSolicitudFile(getDocumentoFileName(), identificador);
        await ProcesosApi.eliminarAnexoProceso(procesoId, docAnexo.id);
      }
      // Notificar al componente padre
      if (docAnexo.id) {
        await onDelete(docAnexo.id);
      }
      // Actualizar estado local
      setHasDocumento(false);
      setDocumentoUrl("");
    } catch (error) {
      console.error("Error eliminando el Documento Anexo :", error);
      setError(`Error al eliminar el Documento Anexo de ${docAnexo.tipo}`);
    } finally {
      setIsLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const renderContent = () => {
    if (hasDocumento) {
      return (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 150,
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body1" color="textSecondary" align="center">
            Doc. Cargado
          </Typography>
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              display: "flex",
              gap: 1,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              padding: "4px",
              borderRadius: "4px",
            }}
          >
            <IconButton size="small" onClick={triggerFileInput} color="info">
              <EditIcon />
            </IconButton>
            <IconButton size="small" onClick={handleDelete} color="error">
              <DeleteIcon />
            </IconButton>
          </Box>
          <Typography
            variant="caption"
            color="primary"
            sx={{ cursor: "pointer", mt: 2 }}
            onClick={() => setShowPdfViewer(true)} // Mostrar el modal al hacer clic
          >
            Ver documento
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        onClick={triggerFileInput}
        sx={{
          width: "100%",
          height: 150,
          border: "2px dashed #ccc",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          backgroundColor: "#f8f9fa",
          transition: "all 0.2s",
          position: "relative",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "#f0f0f0",
          },
        }}
      >
        <AddAPhotoIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
        <Typography color="textSecondary" align="center" sx={{ px: 2 }}>
          {isNew ? "¡Nuevo docAnexo! Click para subir el archivo" : "Click para subir el docAnexo"}
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
          Formato requerido: PDF
        </Typography>
        {isNew && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              display: "flex",
              gap: 1,
            }}
          >
            <IconButton size="small" onClick={handleDelete} color="error">
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Card>
      <SuiBox p={2}>
        <SuiBox mb={2}>
          <SuiTypography variant="h6" fontWeight="medium">
            {docAnexo.tipo}
          </SuiTypography>
        </SuiBox>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {renderContent()}
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleDocumentoChange}
          style={{ display: "none" }}
        />

        {isLoading && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography color="textSecondary">Procesando...</Typography>
          </Box>
        )}

        {/* Modal para visualizar el PDF */}
        <FileDialog
          open={showPdfViewer}
          onClose={() => setShowPdfViewer(false)}
          title={`Documento Anexo - ${docAnexo.tipo}`}
          content={
            <embed
              src={documentoUrl}
              type="application/pdf"
              width="100%"
              height="500px"
              style={{ border: "none" }}
            />
          }
        />
      </SuiBox>
    </Card>
  );
};

DocumentoAnexo.propTypes = {
  docAnexo: PropTypes.shape({
    id: PropTypes.number,
    tipo: PropTypes.string.isRequired,
    nombre_anexo: PropTypes.string,
    isNew: PropTypes.bool,
  }).isRequired,
  procesoId: PropTypes.number.isRequired,
  identificador: PropTypes.string,
  onDelete: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  isNew: PropTypes.bool,
};

export default DocumentoAnexo;
